'use client';

import * as React from 'react';

import { cn } from '@/lib/ui-kit/utils';

interface InputV2Props extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: React.ReactNode;
  helperText?: React.ReactNode;
  error?: React.ReactNode;
  rightSlot?: React.ReactNode;
  wrapperClassName?: string;
}

const InputV2 = React.forwardRef<HTMLInputElement, InputV2Props>(
  (
    {
      label,
      helperText,
      error,
      rightSlot,
      wrapperClassName,
      className,
      onFocus,
      onBlur,
      disabled,
      ...props
    },
    ref,
  ) => {
    const [focused, setFocused] = React.useState(false);
    const hasError = Boolean(error);

    return (
      <div className={cn('flex w-full flex-col gap-1.5', wrapperClassName)}>
        {label && (
          <span
            className={cn(
              'text-[12px] font-normal leading-[1.15] transition-colors',
              focused ? 'text-v2-text-primary' : 'text-v2-text-secondary',
              hasError && 'text-v2-btn-premium-red-light',
            )}
          >
            {label}
          </span>
        )}
        <label
          className={cn(
            'flex h-[44px] cursor-text items-center gap-1 rounded-[10px] bg-v2-btn-secondary px-4 py-3 transition-colors',
            hasError && 'border border-solid border-v2-btn-premium-red-light',
            disabled && 'opacity-60',
          )}
        >
          <input
            {...props}
            ref={ref}
            disabled={disabled}
            onFocus={(e) => {
              setFocused(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              setFocused(false);
              onBlur?.(e);
            }}
            className={cn(
              'flex-1 min-w-0 bg-transparent text-[13px] font-normal leading-[1.5] text-v2-text-primary outline-none placeholder:text-v2-text-secondary',
              className,
            )}
          />
          {rightSlot && <span className="flex shrink-0 items-center gap-2">{rightSlot}</span>}
        </label>
        {(helperText || error) && (
          <span
            className={cn(
              'text-[12px] font-normal leading-[1.15]',
              hasError ? 'text-v2-btn-premium-red-light' : 'text-v2-text-secondary',
            )}
          >
            {error ?? helperText}
          </span>
        )}
      </div>
    );
  },
);

InputV2.displayName = 'InputV2';

export type { InputV2Props };
export { InputV2 };
